/**
 * Zodiac Intelligence Assistant - Analytics Service
 * Centralized analytics tracking for assistant interactions
 */

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Event categories
export const EventCategory = {
  WIDGET: 'widget',
  QUICK_ACTION: 'quick_action',
  MESSAGE: 'message',
  RESPONSE: 'response',
  ERROR: 'error'
};

// Event types
export const EventType = {
  // Widget events
  WIDGET_OPENED: 'widget_opened',
  WIDGET_CLOSED: 'widget_closed',
  WIDGET_MINIMIZED: 'widget_minimized',
  CONVERSATION_CLEARED: 'conversation_cleared',
  
  // Quick action events
  QUICK_ACTION_CLICKED: 'quick_action_clicked',
  
  // Message events
  MESSAGE_SUBMITTED: 'message_submitted',
  
  // Response events
  RESPONSE_RECEIVED: 'response_received',
  
  // Error events
  ASSISTANT_ERROR: 'assistant_error'
};

/**
 * Analytics event payload structure
 */
const createEventPayload = (eventType, eventCategory, data = {}) => ({
  event_type: eventType,
  event_category: eventCategory,
  timestamp: new Date().toISOString(),
  ...data
});

/**
 * Send analytics event to backend (non-blocking)
 */
const sendAnalyticsEvent = async (payload) => {
  try {
    // Fire and forget - don't await or block
    fetch(`${API_URL}/api/intelligence-core/analytics/event`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    }).catch(err => {
      // Silently fail - analytics should never break the assistant
      console.debug('Analytics event failed (non-critical):', err.message);
    });
  } catch (err) {
    // Silently fail
    console.debug('Analytics error (non-critical):', err.message);
  }
};

/**
 * AssistantAnalytics - Main analytics tracking class
 */
class AssistantAnalytics {
  constructor() {
    this.sessionId = null;
    this.userId = null;
    this.isAuthenticated = false;
  }
  
  /**
   * Initialize analytics with session context
   */
  init(sessionId, userId = null, isAuthenticated = false) {
    this.sessionId = sessionId;
    this.userId = userId;
    this.isAuthenticated = isAuthenticated;
  }
  
  /**
   * Get base context for all events
   */
  getBaseContext(pageContext) {
    return {
      session_id: this.sessionId,
      user_id: this.userId,
      is_authenticated: this.isAuthenticated,
      route: pageContext?.route || window.location.pathname,
      page_context: pageContext?.title || 'Unknown',
      preferred_domain: pageContext?.domain || null
    };
  }
  
  // ============================================
  // WIDGET EVENTS
  // ============================================
  
  /**
   * Track widget opened
   */
  trackWidgetOpened(pageContext) {
    const payload = createEventPayload(
      EventType.WIDGET_OPENED,
      EventCategory.WIDGET,
      {
        ...this.getBaseContext(pageContext),
        metadata: {
          quick_actions_count: pageContext?.quickActions?.length || 0
        }
      }
    );
    sendAnalyticsEvent(payload);
  }
  
  /**
   * Track widget closed
   */
  trackWidgetClosed(pageContext, messageCount = 0) {
    const payload = createEventPayload(
      EventType.WIDGET_CLOSED,
      EventCategory.WIDGET,
      {
        ...this.getBaseContext(pageContext),
        metadata: {
          messages_in_session: messageCount
        }
      }
    );
    sendAnalyticsEvent(payload);
  }
  
  /**
   * Track widget minimized
   */
  trackWidgetMinimized(pageContext, messageCount = 0) {
    const payload = createEventPayload(
      EventType.WIDGET_MINIMIZED,
      EventCategory.WIDGET,
      {
        ...this.getBaseContext(pageContext),
        metadata: {
          messages_in_session: messageCount
        }
      }
    );
    sendAnalyticsEvent(payload);
  }
  
  /**
   * Track conversation cleared
   */
  trackConversationCleared(pageContext) {
    const payload = createEventPayload(
      EventType.CONVERSATION_CLEARED,
      EventCategory.WIDGET,
      this.getBaseContext(pageContext)
    );
    sendAnalyticsEvent(payload);
  }
  
  // ============================================
  // QUICK ACTION EVENTS
  // ============================================
  
  /**
   * Track quick action clicked
   */
  trackQuickActionClicked(pageContext, quickAction) {
    const payload = createEventPayload(
      EventType.QUICK_ACTION_CLICKED,
      EventCategory.QUICK_ACTION,
      {
        ...this.getBaseContext(pageContext),
        quick_action_label: quickAction.label,
        quick_action_prompt: quickAction.prompt,
        source_type: 'quick_action',
        metadata: {
          prompt_length: quickAction.prompt?.length || 0
        }
      }
    );
    sendAnalyticsEvent(payload);
  }
  
  // ============================================
  // MESSAGE EVENTS
  // ============================================
  
  /**
   * Track message submitted
   */
  trackMessageSubmitted(pageContext, messageText, isQuickAction = false, quickActionLabel = null) {
    const payload = createEventPayload(
      EventType.MESSAGE_SUBMITTED,
      EventCategory.MESSAGE,
      {
        ...this.getBaseContext(pageContext),
        source_type: isQuickAction ? 'quick_action' : 'manual_input',
        message_length: messageText?.length || 0,
        quick_action_label: quickActionLabel,
        metadata: {}
      }
    );
    sendAnalyticsEvent(payload);
  }
  
  // ============================================
  // RESPONSE EVENTS
  // ============================================
  
  /**
   * Track response received
   */
  trackResponseReceived(pageContext, responseData) {
    const payload = createEventPayload(
      EventType.RESPONSE_RECEIVED,
      EventCategory.RESPONSE,
      {
        ...this.getBaseContext(pageContext),
        request_id: responseData.request_id,
        agent_type: responseData.primary_agent,
        provider: responseData.provider,
        model: responseData.model,
        response_time_ms: responseData.latency_ms,
        success: responseData.success,
        metadata: {
          response_length: responseData.response_text?.length || 0,
          confidence_score: responseData.confidence_score
        }
      }
    );
    sendAnalyticsEvent(payload);
  }
  
  // ============================================
  // ERROR EVENTS
  // ============================================
  
  /**
   * Track assistant error
   */
  trackError(pageContext, errorMessage, actionAttempted = 'message_submission') {
    const payload = createEventPayload(
      EventType.ASSISTANT_ERROR,
      EventCategory.ERROR,
      {
        ...this.getBaseContext(pageContext),
        success: false,
        error_message: errorMessage,
        metadata: {
          action_attempted: actionAttempted
        }
      }
    );
    sendAnalyticsEvent(payload);
  }
}

// Export singleton instance
export const assistantAnalytics = new AssistantAnalytics();

export default assistantAnalytics;
